// Who an invoice is FOR, said in the space a cell or a subtitle actually has.
//
// The document is paid by the partner and it is ABOUT its customers, and those
// are two different facts that the screens kept collapsing into the partner's
// name. `InvoiceListItem.customers` is the second one — distinct end-customer
// names across the invoice's lines, alphabetical, and legitimately empty when
// nothing on the document points at a deal.
//
// This lives in its own module rather than inside either screen because the
// list and the sheet have to agree: a row that reads "Kingsport, Bristol +2"
// and a sheet that then names three customers would be two answers to one
// question. The only thing they differ on is how many names fit, which is the
// argument.

import type { InvoiceListItem } from "@crm/shared";

/** How many names a table cell has room for before it starts counting. */
export const CUSTOMERS_IN_A_CELL = 2;

/** A subtitle is a whole line of prose, so it can afford one more. */
export const CUSTOMERS_IN_A_SENTENCE = 3;

export interface CustomerSummary {
  /** The names that fit, in the order given — which is already alphabetical. */
  readonly shown: readonly string[];
  /** How many did not fit. Zero when they all did. */
  readonly hidden: number;
  /** What to render: the names that fit, plus `+N` when some did not. */
  readonly label: string;
  /**
   * Every name, comma-joined, for a `title` tooltip — null when nothing was
   * truncated, because a tooltip that repeats what is already on screen trains
   * people to stop hovering.
   */
  readonly full: string | null;
  /**
   * The invoice names no customer at all: a partner-wide cost recovery, or a
   * document of standalone lines. A real answer, and the reason no caller may
   * treat an empty `label` as "not loaded yet".
   */
  readonly isPartnerWide: boolean;
}

/**
 * Condense the customer list to `limit` names plus a count of the rest.
 *
 * `+2` rather than an ellipsis: the reader's question at a glance is "how many
 * customers is this one document covering", and a number answers it where a
 * fade does not.
 */
export function summariseCustomers(
  customers: readonly string[],
  limit: number = CUSTOMERS_IN_A_CELL,
): CustomerSummary {
  const shown = customers.slice(0, Math.max(limit, 1));
  const hidden = customers.length - shown.length;
  return {
    shown,
    hidden,
    label: hidden > 0 ? `${shown.join(", ")} +${String(hidden)}` : shown.join(", "),
    full: hidden > 0 ? customers.join(", ") : null,
    isPartnerWide: customers.length === 0,
  };
}

/**
 * Both parties in one phrase: who it is for, then who pays.
 *
 * "Kingsport, Bristol — via Hansen CIS B". The customer leads because that is
 * how these deals are spoken about, and the partner is kept because it is the
 * name on the remittance and the one QuickBooks will show. When nothing on the
 * document points at a deal the phrase inverts to say so out loud, since
 * "— via Hansen CIS B" with a blank in front of it reads as a bug.
 */
export function describeParties(
  invoice: Pick<InvoiceListItem, "customers" | "partner_name">,
  limit: number = CUSTOMERS_IN_A_SENTENCE,
): string {
  const summary = summariseCustomers(invoice.customers, limit);
  return summary.isPartnerWide
    ? `${invoice.partner_name} · partner-wide`
    : `${summary.label} — via ${invoice.partner_name}`;
}
